/**
 * BattleScene — orchestrates combat with clear phases:
 *   intro → playerTurn → enemyTurn → victory/defeat
 *
 * Supports multiple enemies. Player always attacks the first living enemy.
 * Each living enemy attacks once per enemy turn.
 */

declare const Phaser: any;

import { BaseScene } from "../../framework/BaseScene";
import { RunState } from "../data/RunState";
import { WordService } from "../services/WordService";
import { LetterGrid, autoTileSize, maskGridSize } from "./battle/grid";
import { ShapePlacementPanel } from "./battle/grid/ShapePlacementPanel";
import { MONO } from "../character/shapes";
import { BattleHUD, EnemyInfo } from "./battle/BattleHUD";
import { CombatManager } from "./battle/CombatManager";
import { scoreWord } from "./battle/LetterScoring";
import { calculateDamage } from "./battle/DamageCalculator";
import { floatingText, flash, shake, fadeOut, ragdoll, screenShake, deathBurst } from "../../framework/effects";
import { Panel } from "../../framework/Panel";
import { AttackResolver } from "./battle/attacks/AttackResolver";
import { AttackType } from "./battle/attacks/AttackType";
import { PerLetterAttack } from "./battle/attacks/PerLetterAttack";
import { wordXP, enemyKillXP, battleVictoryXP } from "../character/progression/XPTable";
import { CATEGORIES, generateChoicesForCategory, UpgradeCategory } from "../character/progression/LevelUpChoices";
import { adjacencyMultiplier } from "../character/progression/WordBonuses";
import { generateMap } from "../data/MapData";
import * as S from "../character/stats/StatKeys";
import { DevPanel } from "../../framework/DevPanel";

type BattlePhase = "intro" | "playerTurn" | "enemyTurn" | "victory" | "defeat";

export class BattleScene extends BaseScene {
  private run!: RunState;
  private wordService!: WordService;
  private grid!: LetterGrid;
  private hud!: BattleHUD;
  private combat!: CombatManager;
  private attackResolver!: AttackResolver;
  private currentAttackType: AttackType = new PerLetterAttack();
  private isAttacking = false;
  private phase: BattlePhase = "intro";

  private isBoss = false;
  private isElite = false;
  private devPanel: DevPanel | null = null;

  /** XP accumulated during this battle (from valid words). */
  private battleXp = 0;
  /** XP accumulated from killing enemies. */
  private killXp = 0;
  /** Bonus XP awarded on victory. */
  private victoryXpBonus = 0;
  /** Levels gained during this battle's victory sequence. */
  private pendingLevelUps = 0;
  /** Gold earned on victory (stored for deferred reward screen). */
  private victoryGold = 0;

  constructor() {
    super({ key: "BattleScene" });
  }

  preload(): void {
    // Background images
    const bgBase = "/sprites/kenney-bg/";
    for (const k of ["clouds1", "clouds2", "mountain1", "mountain2", "hills1", "hills2"]) {
      this.assets.register("image", `bg_${k}`, `${bgBase}${k}.png`);
    }

    // Impact / hit SFX (5 variants each for variety)
    const impactBase = "/audio/kenney-impact/";
    for (let i = 0; i < 5; i++) {
      this.assets.register("audio", `hit_punch_${i}`, `${impactBase}impactPunch_heavy_00${i}.ogg`);
      this.assets.register("audio", `hit_generic_${i}`, `${impactBase}impactGeneric_light_00${i}.ogg`);
    }

    // RPG SFX
    const rpgBase = "/audio/kenney-rpg/";
    this.assets.register("audio", "sfx_knife1", `${rpgBase}knifeSlice.ogg`);
    this.assets.register("audio", "sfx_knife2", `${rpgBase}knifeSlice2.ogg`);
    this.assets.register("audio", "sfx_coins", `${rpgBase}handleCoins.ogg`);
    this.assets.register("audio", "sfx_coins2", `${rpgBase}handleCoins2.ogg`);
    this.assets.register("audio", "sfx_metal_click", `${rpgBase}metalClick.ogg`);
    this.assets.register("audio", "sfx_chop", `${rpgBase}chop.ogg`);

    this.assets.preload(this);
  }

  onReady(data?: any): void {
    this.run = this.game.registry.get("runState");
    this.wordService = this.game.registry.get("wordService");
    this.isBoss = data?.isBoss || false;
    this.isElite = data?.isElite || false;
    this.battleXp = 0;
    this.killXp = 0;
    this.victoryXpBonus = 0;
    this.pendingLevelUps = 0;
    this.victoryGold = 0;
    this.isAttacking = false;
    this.phase = "intro";
    this.levelUpPanel = null;

    // Register battle sound tones
    this.audio.registerTone("word_valid", { frequency: 440, duration: 0.12, type: "sine", volume: 0.1, slideTo: 660 });
    this.audio.registerTone("word_invalid", { frequency: 300, duration: 0.15, type: "sawtooth", volume: 0.06, slideTo: 180 });

    const player = this.run.player;

    // Combat state (alert if this node is in alertNodes)
    const nodeId = this.run.currentNodeId;
    const isAlert = nodeId ? this.run.alertNodes.has(nodeId) : false;
    const depth: number = data?.depth ?? 0;
    this.combat = new CombatManager(player, this.isBoss, this.isElite, isAlert, depth);

    // Grid layout from player's mask
    const mask = player.gridMask;
    const tileSize = autoTileSize(mask.cols, mask.rows);
    const tileGap = 5;
    const { h: gridH } = maskGridSize(mask, tileSize, tileGap);
    const gameH = 450;
    const bottomPad = 8;
    const gridStartY = gameH - bottomPad - gridH;
    const previewY = gridStartY - LetterGrid.margin - 19;

    // Build enemy info array for HUD
    const enemyInfos: EnemyInfo[] = this.combat.enemies.map(e => ({
      hp: e.hp,
      maxHp: e.maxHp,
      attack: e.attack,
      armor: e.armor,
      label: e.name,
      tier: e.tier,
      level: e.level,
    }));

    // HUD
    this.hud = new BattleHUD(this);
    this.hud.create(
      { hp: player.hp, maxHp: player.maxHp, label: player.name, level: player.level, xpProgress: player.xpProgress },
      enemyInfos,
      this.isBoss,
      this.isElite,
      this.combat.state.ap,
      this.combat.state.maxAp,
      previewY,
      player.gold,
      player.items.count("shuffle"),
      () => this.submitWord(),
      () => this.endTurn(),
      () => this.useShuffle()
    );

    // Attack resolver
    this.attackResolver = new AttackResolver(this, this.hud, this.combat);

    // Letter grid (from player's mask, with dictionary word seeding)
    const wordPicker = (len: number) => this.wordService.randomWordOfLength(len);
    this.grid = new LetterGrid(this, 400, gridStartY, mask, { tileSize, tileGap }, wordPicker);
    this.grid.create(() => this.onSelectionChange());

    // Dev panel
    if (this.devPanel) this.devPanel.destroy();
    this.devPanel = new DevPanel();
    this.devPanel.set("grid", `${mask.cols}x${mask.rows} (${mask.count} tiles)`);
    this.updateDevSeeds();

    // Start with intro phase
    this.beginIntro();
  }

  onUpdate(_time: number, _delta: number): void {}

  // --- Phases ---

  private beginIntro(): void {
    this.phase = "intro";
    this.hud.setPlayerTurnActive(false);

    const names = this.combat.enemies.map(e => e.name);
    const unique = [...new Set(names)];
    let msg: string;
    if (unique.length === 1 && names.length > 1) {
      msg = `${names.length} ${unique[0]}s appear!`;
    } else {
      msg = `${names.join(" & ")} appear${names.length === 1 ? "s" : ""}!`;
    }
    const label = this.isBoss ? "BOSS: " : this.isElite ? "ELITE: " : "";
    const alertLabel = this.combat.isAlert ? " [HIGH ALERT]" : "";
    this.hud.showMessage(`${label}${msg}${alertLabel}`);

    this.time.delayedCall(800, () => this.beginPlayerTurn());
  }

  private beginPlayerTurn(): void {
    this.phase = "playerTurn";
    this.combat.startPlayerTurn();
    this.hud.updateAp(this.combat.state.ap, this.combat.state.maxAp);
    this.hud.setPlayerTurnActive(true);
    this.grid.locked = false;
    this.hud.showMessage("Your turn!");
  }

  private beginEnemyTurn(): void {
    this.phase = "enemyTurn";
    this.hud.showEndTurn(false);
    this.hud.setPlayerTurnActive(false);
    this.grid.locked = true;

    // Each living enemy attacks in sequence
    this.executeEnemySequence(0);
  }

  private beginVictory(): void {
    this.phase = "victory";
    this.run.battlesWon++;
    const gold = this.combat.getReward();
    this.victoryGold = gold;
    const player = this.run.player;

    // Apply gold
    player.addGold(gold);
    this.hud.updateGold(player.gold);

    // Alert adjacent battle/elite nodes (enemies go on high alert)
    this.alertAdjacentNodes();
    // Clear this node's alert (we just fought here)
    if (this.run.currentNodeId) {
      this.run.alertNodes.delete(this.run.currentNodeId);
    }

    // Small completion bonus for winning (kill XP already awarded per enemy)
    const bestTier = this.combat.enemies.reduce((best, e) => {
      const order = { normal: 0, elite: 1, boss: 2 };
      return order[e.tier] > order[best] ? e.tier : best;
    }, "normal" as "normal" | "elite" | "boss");
    const victoryXp = battleVictoryXP(bestTier);
    this.victoryXpBonus = victoryXp;
    const levelsGained = player.addXp(victoryXp);
    this.hud.updateXp(player.xpProgress);
    if (levelsGained > 0) {
      this.pendingLevelUps += levelsGained;
      this.hud.updateLevel(player.level);
      this.showLevelUpIndicator();
    }

    // If there are pending level-ups, show them first (reward screen deferred)
    if (this.pendingLevelUps > 0) {
      this.openLevelUpChoices();
    } else {
      this.showRewardScreen(gold);
    }
  }

  private beginDefeat(): void {
    this.phase = "defeat";
    this.showDefeatScreen();
  }

  // --- Player Turn ---

  /** Get the index of the first living enemy (current target). */
  private getTargetIndex(): number {
    return this.combat.enemies.findIndex(e => e.alive);
  }

  private onSelectionChange(): void {
    if (this.phase !== "playerTurn") return;
    const word = this.grid.getWord();
    this.hud.updateWordPreview(word);
    this.hud.updateSubmitEnabled(word.length >= 2 && this.combat.state.apRemaining && !this.isAttacking);
  }

  private submitWord(): void {
    if (this.phase !== "playerTurn" || this.isAttacking) return;

    const selection = this.grid.getSelection();
    if (selection.length < 2) return;

    const word = this.grid.getWord();

    // Spend AP regardless of validity
    this.combat.spendAp();
    this.hud.updateAp(this.combat.state.ap, this.combat.state.maxAp);

    // Validate
    if (!this.wordService.isValid(word)) {
      this.audio.playSfx("hit_punch_2", 0.5);
      this.hud.showMessage(`"${word}" — not a word!`);
      this.grid.flashInvalid();
      this.grid.clearSelection();
      this.checkApExhausted();
      return;
    }

    this.audio.playSfx("hit_generic_3", 0.5);

    const wordScore = scoreWord(word);
    const chainLinkBonus = this.run.player.skills.perkValue("chain_link", "adjBonusPerPair");
    const adjMult = adjacencyMultiplier(selection, chainLinkBonus);
    const result = calculateDamage(word, wordScore, this.combat.bag, adjMult);

    // XP is awarded after the attack lands (see onAttackComplete)
    const rawXp = wordXP(word, wordScore, adjMult);
    const xp = this.combat.bag.get(S.XP_PERCENT, rawXp);
    const player = this.run.player;

    this.bus.emit("word-submitted", { word, ...result });

    // Show damage breakdown before attack
    this.hud.showDamageBreakdown(word, result);

    // Apply score threshold effects
    const threshold = result.threshold;
    if (threshold) {
      // Floating threshold name
      this.time.delayedCall(200, () => {
        floatingText(this, {
          text: threshold.name,
          x: 400, y: this.hud.combatantY - 70,
          color: threshold.color,
        });
      });

      // Heal
      if (threshold.healAmount > 0) {
        player.heal(threshold.healAmount);
        this.hud.updatePlayerHp(player.hp, player.maxHp);
      }
    }

    // Target the first living enemy
    const targetIdx = this.getTargetIndex();

    // Brief pause so the player can read the breakdown
    this.isAttacking = true;
    const enemy = this.combat.enemies[targetIdx];
    const enemyWasFullHp = enemy.hp === enemy.maxHp;
    this.time.delayedCall(400, () => {
      this.attackResolver.resolve(word, result, this.currentAttackType, targetIdx, {
        onAttackComplete: (targetDead: boolean) => {
          this.isAttacking = false;
          const usedTiles = this.grid.disableUsedTiles();

          // Resurrect perk — chance per tile to come back with a new letter
          const resurrectChance = player.skills.perkValue("resurrect", "resurrectChance");
          if (resurrectChance > 0) {
            const revived = usedTiles.filter(() => Math.random() < resurrectChance);
            if (revived.length > 0) {
              this.time.delayedCall(200, () => {
                this.grid.refreshTiles(revived);
              });
            }
          }

          // Award word XP after the attack lands
          this.battleXp += xp;
          let totalAwarded = xp;
          const slot = this.hud.enemySlots[targetIdx];

          // Award kill XP immediately when enemy dies
          if (targetDead) {
            const killReward = enemyKillXP(enemy.tier, enemy.level, enemyWasFullHp);
            this.killXp += killReward;
            totalAwarded += killReward;
            const killLabel = enemyWasFullHp ? `+${killReward} XP KILL (ONE-SHOT!)` : `+${killReward} XP KILL`;
            this.time.delayedCall(300, () => {
              floatingText(this, {
                text: killLabel,
                x: slot.x, y: slot.y + 15,
                color: enemyWasFullHp ? "#22d3ee" : "#34d399",
                fontSize: enemyWasFullHp ? 16 : 14,
              });
            });
          }

          const levelsGained = player.addXp(totalAwarded);
          this.hud.updateXp(player.xpProgress);
          floatingText(this, {
            text: `+${xp} XP`,
            x: slot.x, y: slot.y + 30,
            color: "#fbbf24",
            fontSize: 14,
          });
          if (levelsGained > 0) {
            this.pendingLevelUps += levelsGained;
            this.hud.updateLevel(player.level);
            this.showLevelUpIndicator();
          }

          if (targetDead) {
            deathBurst(this, slot.x, slot.y);
            ragdoll(this, slot.sprite, {
              onComplete: () => {
                // Check if all enemies are dead
                if (this.combat.allDead) {
                  this.beginVictory();
                } else {
                  this.checkApExhausted();
                }
              },
            });
            return;
          }

          this.checkApExhausted();
        },
      });
    });
  }

  private checkApExhausted(): void {
    if (!this.combat.state.apRemaining) {
      this.hud.updateSubmitEnabled(false);
      this.grid.locked = true;
      this.grid.clearSelection();
    }
  }

  private endTurn(): void {
    if (this.phase !== "playerTurn") return;
    this.beginEnemyTurn();
  }

  private useShuffle(): void {
    if (this.phase !== "playerTurn" || this.isAttacking) return;
    const player = this.run.player;
    if (!player.items.use("shuffle")) return;

    this.grid.clearSelection();
    this.grid.refreshAll();
    this.updateDevSeeds();
    this.hud.updateShuffle(player.items.count("shuffle"));
    this.hud.showMessage("Board shuffled!");
  }

  // --- Enemy Turn ---

  /** Execute enemy attacks one by one in sequence. */
  private executeEnemySequence(fromIndex: number): void {
    // Find the next living enemy starting from fromIndex
    let idx = fromIndex;
    while (idx < this.combat.enemies.length && this.combat.enemies[idx].dead) {
      idx++;
    }

    // All enemies have attacked — back to player turn
    if (idx >= this.combat.enemies.length) {
      this.time.delayedCall(400, () => this.beginPlayerTurn());
      return;
    }

    this.time.delayedCall(300, () => {
      this.executeEnemyAttack(idx, () => {
        this.executeEnemySequence(idx + 1);
      });
    });
  }

  private executeEnemyAttack(enemyIndex: number, onDone: () => void): void {
    const enemy = this.combat.enemies[enemyIndex];
    const slot = this.hud.enemySlots[enemyIndex];

    const playerDead = this.combat.enemyAttackPlayer(enemy);
    const player = this.combat.player;
    this.hud.updatePlayerHp(player.hp, player.maxHp);

    floatingText(this, {
      text: `-${enemy.attack}`,
      x: this.hud.playerX, y: this.hud.combatantY - 50,
      color: "#ef4444",
    });
    flash(this, this.hud.playerSprite);
    shake(this, this.hud.playerSprite);
    screenShake(this, { intensity: 0.005, duration: 100 });

    if (this.grid.activeCount() < 8) {
      this.grid.refreshDisabled();
      this.updateDevSeeds();
    }

    if (playerDead) {
      fadeOut(this, this.hud.playerSprite, {
        alpha: 0.15,
        duration: 500,
        onComplete: () => this.beginDefeat(),
      });
      return;
    }

    this.time.delayedCall(400, onDone);
  }

  // --- Level Up ---

  private levelUpPanel: Panel | null = null;

  /** Show the pulsing level-up icon next to the player. */
  private showLevelUpIndicator(): void {
    this.hud.showLevelUpIcon(() => this.openLevelUpChoices());
  }

  /**
   * Open the level-up panel — Step 1: pick a category.
   * Panel is positioned above the grid in the message/preview area.
   */
  private openLevelUpChoices(): void {
    if (this.levelUpPanel) return;

    // Position near the top, below the combatants
    const panelX = 400;
    const panelY = 120;
    const panelW = 340;
    const panelH = 160;

    const panel = new Panel(this, {
      x: panelX, y: panelY,
      width: panelW, height: panelH,
      fadeDelay: 100,
    });
    this.levelUpPanel = panel;

    const player = this.run.player;
    panel.addText(0, -60, `LEVEL UP!  Lv.${player.level}`, {
      fontSize: "16px", color: "#fbbf24", fontStyle: "bold",
    });
    panel.addText(0, -42, "Choose an upgrade type:", {
      fontSize: "9px", color: "#9ca3af",
    });

    // 4 category buttons in a 2x2 grid with icon badges
    const colSpacing = 150;
    const rowSpacing = 46;
    const startY = -12;
    const badgeSize = 24;

    for (let i = 0; i < CATEGORIES.length; i++) {
      const cat = CATEGORIES[i];
      const col = i % 2;
      const row = Math.floor(i / 2);
      const bx = (col - 0.5) * colSpacing;
      const by = startY + row * rowSpacing;

      const btnColor = parseInt(cat.color.replace("#", ""), 16);
      const btnW = 130;
      const btnH = 32;

      // Row background — clickable
      const rowBg = this.add.rectangle(panelX + bx, panelY + by, btnW, btnH, 0x334155, 0.9);
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on("pointerover", () => rowBg.setFillStyle(0x475569, 0.95));
      rowBg.on("pointerout", () => rowBg.setFillStyle(0x334155, 0.9));
      rowBg.on("pointerdown", () => {
        panel.destroy();
        this.levelUpPanel = null;
        this.openCategoryChoices(cat.category);
      });
      panel.addRaw(rowBg);

      // Icon badge
      const badgeX = panelX + bx - btnW / 2 + 4 + badgeSize / 2;
      const badge = this.add.rectangle(badgeX, panelY + by, badgeSize, badgeSize, btnColor, 0.85);
      panel.addRaw(badge);

      const iconText = this.add.text(badgeX, panelY + by, cat.icon, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px", color: "#ffffff", fontStyle: "bold",
      });
      iconText.setOrigin(0.5);
      panel.addRaw(iconText);

      // Name — right of badge
      const textX = badgeX + badgeSize / 2 + 6;
      const nameText = this.add.text(textX, panelY + by - 5, cat.name, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "11px", color: "#ffffff", fontStyle: "bold",
      });
      nameText.setOrigin(0, 0.5);
      panel.addRaw(nameText);

      // Description — below name
      const descText = this.add.text(textX, panelY + by + 7, cat.description, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "8px", color: "#9ca3af",
      });
      descText.setOrigin(0, 0.5);
      panel.addRaw(descText);
    }

    panel.show();
  }

  /**
   * Step 2: show 3 random choices from the selected category.
   * Grid category skips the 3-choice panel and opens placement UI directly.
   */
  private openCategoryChoices(category: UpgradeCategory): void {
    if (category === "grid") {
      this.openGridPlacement();
      return;
    }

    const panelX = 400;
    const panelY = 130;
    const panelW = 340;
    const panelH = 240;

    const panel = new Panel(this, {
      x: panelX, y: panelY,
      width: panelW, height: panelH,
      fadeDelay: 80,
    });
    this.levelUpPanel = panel;

    const player = this.run.player;
    const catName = CATEGORIES.find(c => c.category === category)?.name ?? category;
    panel.addText(0, -70, `Choose ${catName}`, {
      fontSize: "15px", color: "#fbbf24", fontStyle: "bold",
    });

    const choices = generateChoicesForCategory(category, player);

    if (choices.length === 0) {
      panel.addText(0, 0, "No options available!", {
        fontSize: "12px", color: "#9ca3af",
      });
      panel.addButton(0, 40, "BACK", 0x1e293b, 0x475569, () => {
        panel.destroy();
        this.levelUpPanel = null;
        this.openLevelUpChoices();
      }, 100, 26);
      panel.show();
      return;
    }

    const rowH = 44;
    const rowW = 280;
    const rowSpacing = 52;
    const startY = -36;
    const badgeSize = 32;

    for (let i = 0; i < choices.length; i++) {
      const choice = choices[i];
      const y = startY + i * rowSpacing;
      const btnColor = parseInt(choice.color.replace("#", ""), 16);

      // Row background — clickable
      const rowBg = this.add.rectangle(panelX, panelY + y, rowW, rowH, 0x334155, 0.9);
      rowBg.setInteractive({ useHandCursor: true });
      rowBg.on("pointerover", () => rowBg.setFillStyle(0x475569, 0.95));
      rowBg.on("pointerout", () => rowBg.setFillStyle(0x334155, 0.9));
      rowBg.on("pointerdown", () => {
        choice.apply(player);
        this.hud.updatePlayerHp(player.hp, player.maxHp);
        this.hud.updateLevel(player.level);
        this.hud.updateXp(player.xpProgress);
        panel.destroy();
        this.levelUpPanel = null;
        this.continueLevelUpFlow();
      });
      panel.addRaw(rowBg);

      // Badge — colored square with icon letter(s)
      const badgeX = panelX - rowW / 2 + 6 + badgeSize / 2;
      const badge = this.add.rectangle(badgeX, panelY + y, badgeSize, badgeSize, btnColor, 0.85);
      panel.addRaw(badge);

      // Badge icon text
      const iconText = this.add.text(badgeX, panelY + y, choice.icon, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px", color: "#ffffff", fontStyle: "bold",
      });
      iconText.setOrigin(0.5);
      panel.addRaw(iconText);

      // Name — right of badge, top half
      const textX = badgeX + badgeSize / 2 + 10;
      const nameText = this.add.text(textX, panelY + y - 8, choice.name, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px", color: "#ffffff", fontStyle: "bold",
      });
      nameText.setOrigin(0, 0.5);
      panel.addRaw(nameText);

      // Description — right of badge, bottom half
      const descText = this.add.text(textX, panelY + y + 8, choice.description, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "9px", color: "#d1d5db",
        wordWrap: { width: rowW - badgeSize - 26 },
      });
      descText.setOrigin(0, 0.5);
      panel.addRaw(descText);
    }

    panel.show();
  }

  /** Open shape placement UI for the grid tile upgrade. */
  private openGridPlacement(): void {
    const player = this.run.player;
    const shapeDef = MONO;
    player.shapes.add(shapeDef);

    const placement = new ShapePlacementPanel(
      this,
      player.gridMask,
      shapeDef,
      (updatedMask) => {
        player.gridMask = updatedMask;
        this.continueLevelUpFlow();
      },
    );
    placement.show();
  }

  /** Shared post-level-up logic: decrement counter, loop or finish. */
  private continueLevelUpFlow(): void {
    this.pendingLevelUps--;
    if (this.pendingLevelUps > 0) {
      this.time.delayedCall(200, () => this.openLevelUpChoices());
    } else {
      this.hud.hideLevelUpIcon();
      if (this.phase === "victory") {
        this.showRewardScreen(this.victoryGold);
      }
    }
  }

  // --- Victory / Reward Screen (replaces the grid) ---

  private showRewardScreen(gold: number): void {
    this.hud.showMessage("");
    this.grid.hide();

    const b = this.grid.bounds;
    const panel = new Panel(this, {
      x: b.centerX,
      y: b.top + b.height / 2,
      width: b.height,
      height: b.height,
      fadeDelay: 200,
    });

    const names = this.combat.enemies.map(e => e.name);
    const unique = [...new Set(names)];
    let defeatedText: string;
    if (unique.length === 1 && names.length > 1) {
      defeatedText = `${names.length} ${unique[0]}s defeated`;
    } else {
      defeatedText = `${names.join(" & ")} defeated`;
    }

    panel.addText(0, -40, "Victory!", {
      fontSize: "22px", color: "#fbbf24", fontStyle: "bold",
    });
    panel.addText(0, -5, defeatedText, {
      fontSize: "13px", color: "#9ca3af",
    });
    const totalXp = this.battleXp + this.killXp + this.victoryXpBonus;
    panel.addText(0, 15, `+ ${gold} gold  ·  + ${totalXp} XP`, {
      fontSize: "12px", color: "#a78bfa",
    });
    panel.addText(0, 30, `(${this.battleXp} words · ${this.killXp} kills · ${this.victoryXpBonus} victory)`, {
      fontSize: "9px", color: "#6b7280",
    });
    panel.addButton(0, 55, "CONTINUE", 0x166534, 0x22c55e, () => {
      this.devPanel?.destroy();
      this.devPanel = null;
      this.sm.pop({ outcome: "victory", gold });
    });

    panel.show();
  }

  // --- Defeat Screen (replaces the grid) ---

  private showDefeatScreen(): void {
    this.hud.showMessage("");
    this.grid.hide();

    const b = this.grid.bounds;
    const panel = new Panel(this, {
      x: b.centerX,
      y: b.top + b.height / 2,
      width: b.height,
      height: b.height,
      fadeDelay: 300,
      fadeDuration: 400,
    });

    panel.addText(0, -40, "Defeated", {
      fontSize: "22px", color: "#ef4444", fontStyle: "bold",
    });
    panel.addText(0, 0, `${this.run.player.gold} gold`, {
      fontSize: "13px", color: "#9ca3af",
    });
    panel.addButton(0, 45, "RETURN", 0x991b1b, 0xef4444, () => {
      this.devPanel?.destroy();
      this.devPanel = null;
      this.sm.pop({ outcome: "defeat" });
    });

    panel.show();
  }

  // --- Alert System ---

  private updateDevSeeds(): void {
    if (!this.devPanel) return;
    const seeds = this.grid.seededWords;
    const seedInfo = seeds.length > 0
      ? seeds.map(w => `${w} (${w.length})`).join(", ")
      : "none";
    this.devPanel.set("seeds", seedInfo);
  }

  /** Put adjacent unvisited battle/elite nodes on high alert. */
  private alertAdjacentNodes(): void {
    const nodeId = this.run.currentNodeId;
    if (!nodeId) return;

    const map = generateMap(this.run.mapSeed);
    const currentNode = map.nodes.get(nodeId);
    if (!currentNode) return;

    // Sneak skill reduces alert chance by 10% per level (base 50%)
    const alertReduction = this.run.player.skills.skillValue("sneak", "alertReduction");
    const alertChance = Math.max(0, 0.5 - alertReduction);

    const combatTypes = new Set(["battle", "elite"]);
    for (const connId of currentNode.connections) {
      // Only alert unvisited combat nodes
      if (this.run.visitedNodes.has(connId)) continue;
      const neighbor = map.nodes.get(connId);
      if (!neighbor) continue;
      if (combatTypes.has(neighbor.type) && Math.random() < alertChance) {
        this.run.alertNodes.add(connId);
      }
    }
  }
}
